<template>
  <div class="i-please-wait">
    <div
      v-show="!data.showMain"
      class="intro-content"
    >
      <img
        class="logo"
        :src="logo"
        loading="lazy"
      />

      <div class="main-header">Your responses are saved.</div>

      <iBaseButton
        v-bind="INTRO_BUTTON_STYLE"
        @clicked="handleVideoAndCountdown"
      />

      <div class="mid-header">
        Click the button above to generate your career report – it may take a few minutes.
        <div class="sub-header">
          Our coaches will walk you through what to expect in a brief video preview—feel free to turn up the volume.
        </div>
      </div>
    </div>

    <div
      v-show="data.showMain"
      class="main-content"
    >
      <img
        class="logo"
        :src="logo"
        loading="lazy"
      />

      <div class="main-header">Generating your career report...</div>
      <div :style="{ padding: isDesktop ? '56.25% 0 0 0' : '75% 0 0 0', position: 'relative' }">
        <iframe
          id="vimeo-iframe"
          :src="`https://player.vimeo.com/video/${vimeoIDToShow}?badge=0&amp;muted=1&amp;player_id=0&amp;app_id=58479${isMobile ? '&amp;quality=360p' : ''}`"
          frameborder="0"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
          style="position: absolute; top: 0; left: 0; width: 100%; height: 100%"
        ></iframe>
      </div>

      <div>
        <div
          v-if="isSmAndAbove"
          class="text"
        >
          <div>
            <img :src="data.startRedirect ? iconCheckMark : iconSpinner" />
            {{ LOADING_DATA[data.activeLoader].caption }}
            <span class="count">({{ data.activeLoader + 1 }} of {{ LOADING_DATA.length }})</span>
          </div>
          <div>
            {{ `${data.loaderPercent}%` }}
          </div>
        </div>
        <div class="load">
          <template
            v-for="(loader, index) in LOADING_DATA"
            :key="loader.caption"
          >
            <div class="loading">
              <div
                v-if="!isSmAndAbove"
                class="text"
              >
                <div>
                  <img
                    v-if="data.activeLoader >= index"
                    :src="data.activeLoader > index || data.startRedirect ? iconCheckMark : iconSpinner"
                  />
                  {{ loader.caption }}
                </div>
                <div>
                  {{
                    index === data.activeLoader ? `${data.loaderPercent}%` : data.activeLoader > index ? "100%" : "0%"
                  }}
                </div>
              </div>
              <iBaseLoadingBar
                :fill-color="data.activeLoader < index ? black100 : loader.color"
                bar-width="100%"
                bar-height="10px"
                :infinite="false"
                :is-active="index === data.activeLoader"
                :start="`${data.percentVideoWasUnmutedAt}%`"
                :loading-animation-duration-ms="
                  shouldLengthenLastLoadingBar
                    ? VIDEO_UNMUTED_LOADING_ANIMATION_DURATION_MS
                    : LOADING_ANIMATION_DURATION_MS
                "
              />
            </div>
          </template>
        </div>
      </div>

      <iBaseButton
        v-bind="mainButtonStyle"
        @clicked="goToHello"
      />

      <div
        v-if="data.startRedirect"
        class="teeny"
      >
        {{ `You will be automatically redirected in: ${data.redirectDelay}` }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Player from "@vimeo/player"
import { computed, onBeforeUnmount, onMounted, reactive } from "vue"
import { useRoute } from "vue-router"

// COMPOSABLES
import { useExitModal } from "@/composables/useExitModal"
import { useIsMobile } from "@/composables/useIsMobile"
import useIsScreenSize from "@/composables/useIsScreenSize"
import { useRegisterUser } from "@/composables/useRegisterUser"
import { GlobalFunctions } from "@/helpers/globalFunctions"
import { GlobalVariables } from "@/helpers/globalVariables"

// COMPONENTS
import iBaseButton from "@/components/base/BaseButton.vue"
import iBaseLoadingBar from "@/components/base/BaseLoadingBar.vue"

// ASSETS
import iconCheckMark from "@/assets/icons/icon-green-checkmark.svg"
import iconSpinner from "@/assets/images/spinner.gif"
import logo from "@/assets/logo/logo-dark.svg"

// STYLES
import { amethyst100, black100, bluePurpleGradient, bluePurpleGradientLight, white } from "@/styles/colors"
import { breakpointMd, breakpointSm } from "@/styles/variables"

// ROUTES
const activeRoute = useRoute()

// COMPOSABLES
const isDesktop = useIsScreenSize(breakpointMd)
const isSmAndAbove = useIsScreenSize(breakpointSm)
const { removeExitModal } = useExitModal()
const registerUser = useRegisterUser()
const isMobile = useIsMobile()

// CONSTANTS
let player: any
const LOADING_DATA = [
  {
    caption: "Analyzing your career personality",
    color: "#4a9dff"
  },
  {
    caption: "Identifying your ideal careers",
    color: "#7b87ff"
  },
  {
    caption: "Collecting data on your ideal careers",
    color: "#a46aff"
  },
  {
    caption: "Finalizing your career report",
    color: "#be4fff"
  }
]
const LOADING_ANIMATION_DURATION_MS = 30000
let VIDEO_UNMUTED_LOADING_ANIMATION_DURATION_MS = 120000
const TEST_PURPOSE_VIMEO_ID_MAP = {
  careerSwitch: { mobile: 1025351181, desktop: 1025349705 },
  lost: { mobile: 1025347119, desktop: 1025313354 },
  other: { mobile: 1025355395, desktop: 1025354939 }
}
const INTRO_BUTTON_STYLE = {
  backgroundImage: bluePurpleGradient,
  borderRadius: "8px",
  fontWeight: "500",
  size: "medium",
  text: "Generate My Report",
  textColor: white,
  width: "100%",
  padding: "12px 80px",
  isHoverable: true,
  pulseColor: amethyst100
}

// DATA
const data = reactive({
  activeLoader: 0,
  loaderPercent: 0,
  startRedirect: false,
  redirectDelay: 15,
  videoHasBeenUnmuted: false,
  percentVideoWasUnmutedAt: 0,
  showMain: false
})

//COMPUTED
const mainButtonStyle = computed(() => {
  return {
    backgroundImage: data.startRedirect ? bluePurpleGradient : bluePurpleGradientLight,
    borderRadius: "8px",
    fontWeight: "500",
    size: isDesktop.value ? "medium" : "small",
    text: "Get My Report",
    textColor: white,
    width: "100%",
    padding: isDesktop.value ? "12px 120px" : "12px 80px",
    isDisabled: !data.startRedirect,
    isHoverable: !data.startRedirect,
    pulseColor: !data.startRedirect ? "" : amethyst100
  }
})
const shouldLengthenLastLoadingBar = computed(
  () => data.activeLoader === LOADING_DATA.length - 1 && data.videoHasBeenUnmuted
)
const vimeoIDToShow = computed(() => {
  const screenWidth = isDesktop.value ? "desktop" : "mobile"
  const testPurposeFromUrl = activeRoute.query["test_purpose"]?.toString() as keyof typeof TEST_PURPOSE_VIMEO_ID_MAP
  const testTakingPurposeKey =
    !!testPurposeFromUrl && Object.hasOwn(TEST_PURPOSE_VIMEO_ID_MAP, testPurposeFromUrl) ? testPurposeFromUrl : "other"

  return TEST_PURPOSE_VIMEO_ID_MAP[testTakingPurposeKey][screenWidth]
})

// FUNCTIONS
async function goToHello() {
  const baseUrl = `${window.location.protocol}//${window.location.host}`
  const pageUrl = GlobalVariables.urls.hello.path
  GlobalFunctions.redirectToPage({ baseUrl, pageUrl })
}

const startLoader = async () => {
  for (let i = 0; i < LOADING_DATA.length; i++) {
    data.activeLoader = i
    data.loaderPercent = 0

    // Load 100% for each loading bar
    while (data.loaderPercent < 100) {
      // Use longer timer for last loading bar if video has been unmuted
      const durationMs = shouldLengthenLastLoadingBar.value
        ? VIDEO_UNMUTED_LOADING_ANIMATION_DURATION_MS
        : LOADING_ANIMATION_DURATION_MS
      const percentRemaining = shouldLengthenLastLoadingBar.value ? 100 - data.percentVideoWasUnmutedAt : 100

      await GlobalFunctions.delayInMiliseconds(durationMs / percentRemaining)
      data.loaderPercent++
    }
  }
  localStorage.setItem("report-loading-completed", "true")

  // Show redirect screen and start countdown to redirect to hello
  removeExitModal()
  data.startRedirect = true
  while (data.redirectDelay > 0) {
    await GlobalFunctions.delayInMiliseconds(1000)
    data.redirectDelay--
  }
  goToHello()
}

const onUnmutedListener = () => {
  player.getMuted().then((muted: boolean) => {
    // Check that this event unmutes the video and that it hasn't been unmuted before
    if (!muted && !data.videoHasBeenUnmuted) {
      data.videoHasBeenUnmuted = true

      // If unmuted during last slide, set the percent at which it was unmuted and the remaining time it should run for
      if (data.activeLoader === LOADING_DATA.length - 1) {
        data.percentVideoWasUnmutedAt = data.loaderPercent
        VIDEO_UNMUTED_LOADING_ANIMATION_DURATION_MS -=
          (data.percentVideoWasUnmutedAt / 100) * LOADING_ANIMATION_DURATION_MS
      }
    }
  })
}

const onVideoReady = () => {
  player.ready().then(() => {
    player.getDuration().then((duration: number) => {
      VIDEO_UNMUTED_LOADING_ANIMATION_DURATION_MS = Math.round((duration || 210) * 1000 - 90000)
    })
    player.on("volumechange", onUnmutedListener)
  })
}

const handleVideoAndCountdown = () => {
  data.showMain = true

  if (player) {
    player.setCurrentTime(0)
    player.play()
    player.setMuted(false)
  }

  startLoader()
}

const initialCountdown = async () => {
  await GlobalFunctions.delayInMiliseconds(10000)

  if (!data.showMain) {
    data.showMain = true

    if (player) {
      player.setCurrentTime(0)
      player.setMuted(true)
      player.play()
    }

    startLoader()
  }
}

// LIFECYCLE HOOKS
onMounted(() => {
  // Register the user while the loading screen is displayed
  registerUser()

  if (localStorage.getItem("report-loading-completed") === "true") goToHello()

  const iframe = document.querySelector("#vimeo-iframe")
  player = new Player(iframe)
  onVideoReady()

  initialCountdown()
})

onBeforeUnmount(() => {
  player.off("volumechange", onUnmutedListener)
})
</script>

<style lang="scss" scoped>
.i-please-wait {
  background-color: $white;
  padding: 15px 20px;

  @include flex(row, center, center);

  @media screen and (min-width: $breakpoint-sm) {
    padding: 30px;
  }

  .intro-content,
  .main-content {
    max-width: 750px;
    width: 100%;
    background-color: white;

    @include flex(column, center, stretch, $gap: 15px);

    .logo {
      width: 150px;
      height: 40px;
      margin: auto;
    }

    .main-header {
      @include text-xl;

      font-weight: 700;
      text-align: center;

      @media screen and (min-width: $breakpoint-sm) {
        @include display-sm;

        margin: 20px 0 25px;
      }
    }

    .sub-header {
      @include text-md;

      font-weight: 400;
      margin: 6px auto auto;
    }

    .text {
      @include flex(row, space-between, center, $gap: 5px);
      @include text-sm;

      font-weight: 600;
      margin-bottom: 12px;

      @media screen and (min-width: $breakpoint-sm) {
        @include text-md;
      }

      img {
        width: 18px;
        height: 18px;
        margin-right: 3px;
        vertical-align: middle;
      }

      .count {
        margin-left: 2px;
        color: $black400;

        @include text-xs;

        font-weight: 400;

        @media screen and (min-width: $breakpoint-sm) {
          margin-left: 5px;

          @include text-sm;
        }
      }
    }

    .load {
      @include flex(column, center, stretch, $gap: 12px);

      @media screen and (min-width: $breakpoint-sm) {
        @include flex(row, center, stretch, $gap: 8px);

        width: 100%;
      }

      .loading {
        @media screen and (min-width: $breakpoint-sm) {
          width: 25%;
        }
      }
    }

    .teeny {
      @include text-xs;

      text-align: center;

      @media screen and (min-width: $breakpoint-sm) {
        @include text-sm;
      }
    }
  }

  .intro-content {
    max-width: 650px;
    text-align: center;

    .mid-header {
      @include text-md;

      font-weight: 700;
    }

    .sub-header {
      max-width: 600px;
    }
  }
}
</style>
